import React from 'react'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'
import { parse } from 'qs'
import { useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom'

// redux
import * as authModule from '../../redux/modules/authentication'

// styles
import {
  ContentContainer,
  DashboardContainer,
  DashboardOverWrapper,
  DashboardWrapper,
  DashLayoutContentContainer
} from './styles'

// utils and config
import l, { isBp, isGs } from 'config/localization'
import constants from 'config/constants'
import isLoggedIn from 'utils/auth'

// load ModalsCanvas and Navbar synchronously to avoid scroll bug
// where Y scroll position has to be > 0 to show modal
import AppLoadable from 'components/AppLoadable'
import DashboardSidebar from 'components/DashboardSidebar'
import EmailConfirmationBanner from 'components/EmailConfirmationBanner'
import ModalsCanvas from 'components/Modals/ModalsCanvas'
import Navbar from 'components/Navbar'

const ConditionalDisplay = AppLoadable({
  loader: () => import('components/ConditionalDisplay/ConditionalDisplay')
})
const Header = AppLoadable({
  loader: () => import('components/Header')
})

// keyboard key codes
const ESCAPE_KEYCODE = 27
const ONE_KEYCODE = 49
const TWO_KEYCODE = 50
const THREE_KEYCODE = 51
const FOUR_KEYCODE = 52
const FIVE_KEYCODE = 53
const SIX_KEYCODE = 54

const determineContentAccess = props => {
  const { onlyGodRole, hasGodRole, isLoading, hasSuperAdminRole, superOrGodRole } = props

  if (onlyGodRole && !hasGodRole && !isLoading) return true
  if (superOrGodRole && !hasSuperAdminRole && !hasGodRole && !isLoading) return true

  return false
}

const getLocationInfo = ({ location }) => {
  const path = location.pathname
  const search = location.search

  // Growsmart
  const provCampaignsActive =
    path.includes(`/dashboard/competitions`) && search.includes('comptype=provincial')
  const subjCampaignsActive =
    path.includes(`/dashboard/competitions`) && search.includes('comptype=subject')

  const homeActive = path.match(/\/dashboard$/)
  const rulesActive = path.includes('/dashboard/rules')
  const emailActivityActive = path.includes('/dashboard/email-activity')
  const singleEntityActive = path.match(/\/dashboard\/\w+\/\d/)
  const roundsActive = path.includes(`/dashboard/rounds`)
  const workshopsActive = path.includes(`/dashboard/workshops`)
  const campaignsActive = path.includes(`/dashboard/competitions`)
  const activeStoriesPath = path.includes(`/dashboard/stories`)
  const activeContributionsPath = path.includes(`/dashboard/contributions`)
  const activePledgesPath = path.includes(`/dashboard/pledges`)
  const organisationsActive = path.includes(`/dashboard/organisations`)
  const scholarshipsActive = path.includes('/dashboard/scholarships')
  const institutionsActive = path.includes('/dashboard/institutions')
  const sponsorshipsActive = path.includes('/dashboard/sponsorships')
  const peopleActive = path.match(/dashboard\/people/)
  const teamsActive = path.match(/dashboard\/teams/)
  const emailActivityPathActive = path.match(/dashboard\/email-activity/)
  const platformEventsPathActive = path.match(/dashboard\/platform-events/)
  const billingActive = path.match(/dashboard\/billing/)
  const accountActive = path.match(/dashboard\/account/)
  const boostsActive = path.match(/dashboard\/boosts/)
  const rsvpsActive = path.match(/dashboard\/rsvps/)
  const enrolmentsActive = path.match(/dashboard\/enrolments/)
  const registrationsActive = path.match(/dashboard\/registrations/)
  const newStoryPath = path.match(/dashboard\/stories\/new/)

  return {
    accountActive,
    activeStoriesPath,
    activeContributionsPath,
    activePledgesPath,
    boostsActive,
    billingActive,
    enrolmentsActive,
    emailActivityActive,
    workshopsActive,
    homeActive,
    rulesActive,
    roundsActive,
    campaignsActive,
    provCampaignsActive,
    subjCampaignsActive,
    organisationsActive,
    institutionsActive,
    scholarshipsActive,
    sponsorshipsActive,
    peopleActive,
    rsvpsActive,
    singleEntityActive,
    teamsActive,
    platformEventsPathActive,
    emailActivityPathActive,
    registrationsActive,
    newStoryPath
  }
}

const scrollToPlace = () => {
  const menu = document.getElementById('dashboard-menu-items-wrapper')
  const activeItem = document.getElementById('active-dash-nav-item')
  if (!menu || !activeItem) return true

  // 30 = 15px padding on the sides + 15px to show snippet of previous nav item
  const scrollOffset = activeItem.offsetLeft - 30

  menu.scroll({ left: scrollOffset })
}

const deriveMaxWidth = ({ location }) => {
  const { pathname } = location
  const fullWidthPaths = ['/dashboard/enrolments']
  if (fullWidthPaths.includes(pathname)) return '100%'

  return '1500px'
}

function withNavigation(Component) {
  return function NavigationWrappedComponent(props) {
    const navigate = useNavigate()
    const location = useLocation()

    return <Component {...props} location={location} navigate={navigate} />
  }
}

class DashboardLayout extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      discoverDropdownVisible: false,
      pricingDropdownVisible: false,
      scrollY: 0,
      visibleModal: null
    }

    this.escFunction = this.escFunction.bind(this)
    this.closeAllModals = this.closeAllModals.bind(this)
    this.toggleMenuModal = this.toggleMenuModal.bind(this)
    this.toggleDiscoverDropdown = this.toggleDiscoverDropdown.bind(this)
    this.togglePricingDropdown = this.togglePricingDropdown.bind(this)
  }

  closeAllModals() {
    this.setState({ visibleModal: null })
  }

  toggleMenuModal(value) {
    const { visibleModal } = this.state
    const forceClose = value === 'close'
    this.setState({ visibleModal: visibleModal ? null : forceClose ? null : 'menu-modal' })
  }

  togglePricingDropdown() {
    this.setState({ pricingDropdownVisible: !this.state.pricingDropdownVisible })
  }

  toggleDiscoverDropdown() {
    this.setState({ discoverDropdownVisible: !this.state.discoverDropdownVisible })
  }

  escFunction(event) {
    // This feature is not for Growsmart
    if (!isBp) return true

    const loggedIn = isLoggedIn()

    // determine if there's any open modal
    const anyModalIsOpen = this.state.visibleModal !== null
    const menuModalIsOpen = this.state.visibleModal === 'menu-modal'

    // Any modal that is currently open will be closed
    if (anyModalIsOpen && event.keyCode === ESCAPE_KEYCODE) {
      // first close modals
      this.closeAllModals()

      // then scroll to previous Y position
      window.scrollTo({ top: this.state.scrollY })

      return true
    }

    // No modal is open so escape should open the menu modal
    if (event.keyCode === ESCAPE_KEYCODE) {
      this.setState({ scrollY: window.scrollY, visibleModal: 'menu-modal' })
    }

    if (menuModalIsOpen && loggedIn && event.keyCode === ONE_KEYCODE) {
      // Keyboard shortcut for key '1'
      this.setState({ visibleModal: null })
      this.props.navigate(`/dashboard`)
    }

    if (menuModalIsOpen && loggedIn && event.keyCode === TWO_KEYCODE) {
      // Keyboard shortcut for key '2'
      this.setState({ visibleModal: null })

      const { userData } = this.props
      const profilePath = userData.username ? `/@${userData.username}` : `/users/${userData.id}`

      this.props.navigate(profilePath)
    }

    if (menuModalIsOpen && loggedIn && event.keyCode === THREE_KEYCODE) {
      // Keyboard shortcut for key '3'
      this.setState({ visibleModal: null })
      this.props.navigate(`/dashboard/stories/new?eventType=GoodDeed`)
    }

    if (menuModalIsOpen && event.keyCode === FOUR_KEYCODE) {
      // Keyboard shortcut for key '4'
      this.setState({ visibleModal: null })
      this.props.navigate(`/search/${l('ROUTE_OPPS')}`)
    }

    if (menuModalIsOpen && event.keyCode === FIVE_KEYCODE) {
      // Keyboard shortcut for key '5'
      this.setState({ visibleModal: null })
      this.props.navigate(`/nonprofits`)
    }

    if (menuModalIsOpen && event.keyCode === SIX_KEYCODE) {
      // Keyboard shortcut for key '6'
      this.setState({ visibleModal: null })
      this.props.navigate(`/search/${l('ROUTE_ORGS')}`)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.escFunction, false)
  }

  componentDidMount() {
    document.addEventListener('keydown', this.escFunction, false)

    const { autologin } = parse(location.search.replace('?', ''))

    setTimeout(() => {
      scrollToPlace()
    }, 500)

    if (isLoggedIn()) {
      this.props.fetchMyRoles()
    } else {
      this.props.attemptAutoLogin({ signInToken: autologin })
    }
  }

  // componentDidUpdate(prevProps) {
  //   const prevUrl = prevProps.location.pathname + prevProps.location.search
  //   const currentUrl = this.props.location.pathname + this.props.location.search

  //   if (currentUrl !== prevUrl) {
  //     // to re-render
  //     this.setState({ dashboardPath: currentUrl })
  //   }
  // }

  render() {
    const { discoverDropdownVisible, pricingDropdownVisible, visibleModal } = this.state
    const {
      anythingIsSubmitting,
      anythingIsLoading,
      gsIsDownForMaintenance,
      isLoading,
      onlyGodRole,
      superOrGodRole,
      roleData = [],
      userData = {},
      logout,
      requestConfirmationEmail,
      schoolStatsProvided,
      isPrincipal
    } = this.props

    const { location } = window
    const { autologin } = parse(location.search.replace('?', ''))
    const loggedIn = isLoggedIn()
    const locationInfo = getLocationInfo({ location })

    if (!loggedIn && !autologin) {
      const redirectPath = `/login?redirectTo=${location.href}`
      return <Navigate to={redirectPath} replace />
    }

    const isDashHome = this.props.location.pathname === '/dashboard'
    const isSchoolStatsPage = this.props.location.pathname.match(
      /\/dashboard\/organisations\/\d+\/?/
    )

    if (!schoolStatsProvided && isPrincipal && !isDashHome && !isSchoolStatsPage) {
      const redirectPath = `/dashboard`
      return <Navigate to={redirectPath} replace />
    }

    const hasGodRole = roleData.some(role => role.role_type === constants.ROLES.GOD_MODE)
    const hasSuperAdminRole = roleData.some(role => role.role_type === constants.ROLES.SUPERADMIN)
    const emailNeedsConfirming = userData.email && !userData.email_confirmed
    const maxWidth = deriveMaxWidth({ location })

    const cannotViewContent = determineContentAccess({
      onlyGodRole,
      hasGodRole,
      isLoading,
      hasSuperAdminRole,
      superOrGodRole
    })

    if (cannotViewContent) {
      return (
        <DashboardWrapper>
          <DashboardSidebar
            roles={roleData}
            user={userData}
            locationInfo={locationInfo}
            logout={logout}
          />

          <DashboardContainer>
            <ContentContainer>You are not permitted to view this content.</ContentContainer>
          </DashboardContainer>
        </DashboardWrapper>
      )
    }

    if (isGs && gsIsDownForMaintenance) {
      return (
        <DashboardWrapper>
          <DashboardSidebar
            roles={roleData}
            user={userData}
            locationInfo={locationInfo}
            logout={logout}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              flexDirection: 'column',
              fontSize: '15px'
            }}
          >
            <div>We are temporarily down for some maintenance.</div>

            <div style={{ marginTop: '10px' }}>We apologize for any inconvenience.</div>
          </div>
        </DashboardWrapper>
      )
    }

    const displayBpNavbar = true
    const displayFormBar = false

    // const displayBpNavbar = shouldShowNavBar({ locationInfo })
    // const displayFormBar = shouldShowFormBar({ locationInfo })
    const displayGsHeader = isGs

    return (
      <DashboardOverWrapper visibleModal={visibleModal}>
        <DashboardWrapper isHomePage={locationInfo.homeActive}>
          <ConditionalDisplay displayWhen={[displayBpNavbar]}>
            <Navbar
              isLoading={anythingIsLoading || anythingIsSubmitting}
              discoverDropdownVisible={discoverDropdownVisible}
              pricingDropdownVisible={pricingDropdownVisible}
              toggleDiscoverDropdown={this.toggleDiscoverDropdown}
              toggleMenuModal={this.toggleMenuModal}
              togglePricingDropdown={this.togglePricingDropdown}
            />
          </ConditionalDisplay>

          <ConditionalDisplay displayWhen={[displayGsHeader]}>
            <Header
              dashboard
              loggedIn
              logout={logout}
              userData={userData}
              roleData={roleData}
              isLoading={anythingIsLoading || anythingIsSubmitting}
            />
          </ConditionalDisplay>

          <Helmet title={l('SITENAME')} />

          <DashboardContainer displayFormBar={displayFormBar} isHomePage={locationInfo.homeActive}>
            <DashLayoutContentContainer
              displayFormBar={displayFormBar}
              maxWidth={maxWidth}
              padTop={emailNeedsConfirming}
            >
              {emailNeedsConfirming && (
                <EmailConfirmationBanner
                  displayFormBar={displayFormBar}
                  requestConfirmationEmail={() => requestConfirmationEmail(userData)}
                />
              )}

              <Outlet />
            </DashLayoutContentContainer>
          </DashboardContainer>

          <DashboardSidebar
            displayBpNavbar={displayBpNavbar}
            displayFormBar={displayFormBar}
            displayGsHeader={displayGsHeader}
            roles={roleData}
            user={userData}
            locationInfo={locationInfo}
            logout={logout}
          />
        </DashboardWrapper>

        <ModalsCanvas
          closeAllModals={this.closeAllModals}
          loggedIn={loggedIn}
          logout={logout}
          userData={userData}
          visibleModal={visibleModal}
        />
      </DashboardOverWrapper>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  fetchMyRoles: () => dispatch(authModule.fetchMyRoles()),
  attemptAutoLogin: key => dispatch(authModule.attemptAutoLogin(key)),
  requestConfirmationEmail: userEmail => dispatch(authModule.requestConfirmationEmail(userEmail)),
  logout: () => dispatch(authModule.logout())
})

const mapStateToProps = state => {
  const roleData = state.authentication.roleData || []
  // const hasGodAdminRole = roleData.some(r => r.role_type === constants.ROLES.GOD_MODE)
  // const gsIsDownForMaintenance = !hasGodAdminRole

  return {
    gsIsDownForMaintenance: false,
    roleData,
    isPrincipal: state.authentication.isPrincipal,
    schoolStatsProvided: state.authentication.schoolStatsProvided,
    userData: state.authentication.userData,
    anythingIsLoading:
      state.authentication.isLoading ||
      state.applications.isLoading ||
      state.campaigns.isLoading ||
      state.opportunities.isLoading ||
      state.organisations.isLoading ||
      state.stories.isLoading ||
      state.people.isLoading,
    anythingIsSubmitting:
      state.authentication.isSubmitting ||
      state.applications.isSubmitting ||
      state.campaigns.isSubmitting ||
      state.opportunities.isSubmitting ||
      state.stories.isSubmitting ||
      state.organisations.isSubmitting ||
      state.people.isSubmitting
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withNavigation(DashboardLayout))
